const SEARCH_TAB_TRANSIENT_PARAMS = ['demoTour', 'demoFeature', 'demoStep'] as const;
const SEARCH_DEMO_TAB_ID_KEY = 'disgenet_search_demo_tab_id';

export function normalizeSearch(search: string): string {
  if (!search) return '';
  return search.startsWith('?') ? search : `?${search}`;
}

export function stripDemoTourParams(search: string): string {
  const normalized = normalizeSearch(search);
  if (!normalized) return '';
  const params = new URLSearchParams(normalized.slice(1));
  let changed = false;
  for (const key of SEARCH_TAB_TRANSIENT_PARAMS) {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  }
  if (!changed) return normalized;
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function searchHasDemoTour(search: string): boolean {
  const params = new URLSearchParams(normalizeSearch(search).slice(1));
  return params.get('demoTour') === '1';
}

export function rememberSearchDemoTabId(id: string) {
  try {
    sessionStorage.setItem(SEARCH_DEMO_TAB_ID_KEY, id);
  } catch {
    // Ignore quota / private-mode failures; the tab will still lose demoTour params.
  }
}

export function takeSearchDemoTabId(): string | null {
  try {
    const id = sessionStorage.getItem(SEARCH_DEMO_TAB_ID_KEY);
    if (id) sessionStorage.removeItem(SEARCH_DEMO_TAB_ID_KEY);
    return id;
  } catch {
    return null;
  }
}

/**
 * Core search identity for tab deduplication.
 * Ignores filters, mapping, source and other transient URL state.
 * Idents are order-insensitive so multi-select matches reliably.
 */
export function getSearchTabIdentity(search: string): string {
  const params = new URLSearchParams(normalizeSearch(search).slice(1));
  const view = (params.get('view') ?? '').toUpperCase();
  const tab = (params.get('tab') ?? '').toUpperCase();
  const identsRaw = params.get('idents') ?? '';
  const idents =
    identsRaw === 'ALL' || identsRaw === ''
      ? identsRaw
      : identsRaw.split('-').filter(Boolean).sort().join('-');
  return `view=${view}&idents=${idents}&tab=${tab}`;
}

export function searchesShareIdentity(a: string, b: string): boolean {
  return getSearchTabIdentity(a) === getSearchTabIdentity(b);
}

export function searchHasAppliedFilters(search: string): boolean {
  const params = new URLSearchParams(normalizeSearch(search).slice(1));
  if (params.get('f')) return true;
  for (const key of params.keys()) {
    if (key.startsWith('filters-') || key.startsWith('filters[')) return true;
  }
  return false;
}

export function searchHasGraphsDisplay(search: string): boolean {
  const params = new URLSearchParams(normalizeSearch(search).slice(1));
  return params.get('display') === 'graphs';
}

export function getUrlFilterSignature(search: string): string {
  const params = new URLSearchParams(normalizeSearch(search).slice(1));
  const parts: string[] = [];
  const filterToken = params.get('f');
  if (filterToken) parts.push(`f=${filterToken}`);
  for (const [key, value] of params.entries()) {
    if (key.startsWith('filters-') || key.startsWith('filters[')) {
      parts.push(`${key}=${value}`);
    }
  }
  return parts.sort().join('&');
}

export function isCleanAllResultsSearch(search: string): boolean {
  const params = new URLSearchParams(normalizeSearch(search).slice(1));
  const view = (params.get('view') ?? '').toUpperCase();
  const idents = (params.get('idents') ?? '').toUpperCase();
  if (view !== 'ALL' || idents !== 'ALL') return false;
  return !searchHasAppliedFilters(search);
}

/** Same entity (view + idents), ignoring association tab, source and filters. */
export function searchesShareEntity(a: string, b: string): boolean {
  const parse = (search: string) => {
    const params = new URLSearchParams(normalizeSearch(search).slice(1));
    const view = (params.get('view') ?? '').toUpperCase();
    const identsRaw = params.get('idents') ?? '';
    const idents =
      identsRaw === 'ALL' || identsRaw === ''
        ? identsRaw
        : identsRaw.split('-').filter(Boolean).sort().join('-');
    return `view=${view}&idents=${idents}`;
  };
  return parse(a) === parse(b);
}
